import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { WarehouseSelect } from '../ui/WarehouseSelect';
import {
  Add as AddIcon,
  CheckCircle as OkIcon,
  Close as CloseIcon,
  Code as CodeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  FactCheck as ValidateIcon,
  MenuBook as DocsIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon,
  CloudUpload as PushIcon,
} from '@mui/icons-material';
import { ZoneSnapshotEditorDialog } from '../ZoneSnapshotEditorDialog';
import ZoneStatusBadge from './ZoneStatusBadge';
import ZoneTable from './ZoneTable';
import { CONFIG_SCOPE_LABELS, CONFIG_STATE_LABELS } from '../../utils/configVocabulary';

type ZonesPageContentProps = {
  canReadZones: boolean;
  canWriteZones: boolean;
  zoneSummary: any;
  setSnapshotEditorOpen: (value: boolean) => void;
  handleOpenCreate: () => void;
  handleOpenPush: () => void;
  setPageMenuAnchor: (value: HTMLElement | null) => void;
  pageMenuAnchor: HTMLElement | null;
  siteComplexity: any;
  refreshing: boolean;
  loading: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: any) => void;
  typeFilter: string;
  setTypeFilter: (value: string) => void;
  zoneTypeOptions: string[];
  snapshotFilter: string;
  setSnapshotFilter: (value: any) => void;
  policyFilter: string;
  setPolicyFilter: (value: any) => void;
  policiesLoaded: boolean;
  clearFilters: () => void;
  zonesLoaded: boolean;
  filteredZoneRows: any[];
  selectedZone: any;
  handleZoneSelect: (zone: any) => void;
  setZoneMenuAnchor: (value: HTMLElement | null) => void;
  setZoneMenuZone: (zone: any) => void;
  createPolicyForZone: (zone: any) => void;
  zoneRows: any[];
  linesExpanded: boolean;
  setLinesExpanded: (value: boolean) => void;
  lines: any[];
  policyUsage: any;
  formatDateTime: (value?: string | null) => string;
  goToPolicies: (params: Record<string, string>) => void;
  handleCloseZoneDrawer: () => void;
  selectedZoneRow: any;
  selectedZoneStatus: any;
  selectedZoneLoading: boolean;
  titleCase: (value: string) => string;
  handleValidateZone: (zone: any) => void;
  openZonePolicies: (zone: any) => void;
  handleOpenEdit: (zone: any) => void;
  handleDuplicateZone: (zone: any) => void;
  openPushForZone: (zone?: any) => void;
  beginDeleteZone: (zone: any) => void;
  selectedZonePolicies: any[];
  classError: string | null;
  zoneClassValue: string;
  setZoneClassValue: (value: any) => void;
  riskLevelValue: string;
  setRiskLevelValue: (value: any) => void;
  classSaving: boolean;
  saveClassification: () => void;
  cadenceError: string | null;
  cadenceValue: string;
  setCadenceValue: (value: any) => void;
  cadenceSaving: boolean;
  saveCadence: () => void;
  openApplyFeature: () => void;
  zoneFeatures: any[];
  removeAssignment: (assignmentId: string) => void;
  applyFeatureOpen: boolean;
  setApplyFeatureOpen: (value: boolean) => void;
  applyTemplateId: string;
  setApplyTemplateId: (value: string) => void;
  featureTemplates: any[];
  setApplyMinDwellMs: (value: number) => void;
  setApplyCooldownMs: (value: number) => void;
  setApplySeverity: (value: string) => void;
  applyMinDwellMs: number;
  applyCooldownMs: number;
  applySeverity: string;
  applyEnabled: boolean;
  setApplyEnabled: (value: boolean) => void;
  applyDeployNow: boolean;
  setApplyDeployNow: (value: boolean) => void;
  applyingFeature: boolean;
  applyFeature: () => void;
  snapshotEditorOpen: boolean;
  SITE_ID: string;
  initialDeviceId?: string;
  initialCameraId?: string;
  editorOpen: boolean;
  handleCloseEditor: () => void;
  editorMode: string;
  editingZone: any;
  yamlError: string | null;
  yamlContent: string;
  setYamlContent: (value: string) => void;
  handleSaveZone: () => void;
  pushDialogOpen: boolean;
  setPushDialogOpen: (value: boolean) => void;
  pushDeviceId: string;
  setPushDeviceId: (value: string) => void;
  pushing: boolean;
  handlePushToEdge: () => void;
  fetchData: (force?: boolean) => void;
  showDisabledZones: boolean;
  setShowDisabledZones: React.Dispatch<React.SetStateAction<boolean>>;
  pauseAutoRefresh: boolean;
  setPauseAutoRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  returnTo: string;
  handleBackFromZones: () => void;
  zoneMenuAnchor: HTMLElement | null;
  zoneMenuZone: any;
  deleteDialogOpen: boolean;
  deleteSubmitting: boolean;
  setDeleteDialogOpen: (value: boolean) => void;
  zonePendingDelete: any;
  setZonePendingDelete: (zone: any) => void;
  handleDeleteZone: (zone: any) => void;
  snackbar: any;
  setSnackbar: React.Dispatch<React.SetStateAction<any>>;
};

const ZonesPageContent: React.FC<ZonesPageContentProps> = ({
  canReadZones,
  canWriteZones,
  zoneSummary,
  setSnapshotEditorOpen,
  handleOpenCreate,
  handleOpenPush,
  setPageMenuAnchor,
  pageMenuAnchor,
  siteComplexity,
  refreshing,
  loading,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  typeFilter,
  setTypeFilter,
  zoneTypeOptions,
  snapshotFilter,
  setSnapshotFilter,
  policyFilter,
  setPolicyFilter,
  policiesLoaded,
  clearFilters,
  zonesLoaded,
  filteredZoneRows,
  selectedZone,
  handleZoneSelect,
  setZoneMenuAnchor,
  setZoneMenuZone,
  createPolicyForZone,
  zoneRows,
  linesExpanded,
  setLinesExpanded,
  lines,
  policyUsage,
  formatDateTime,
  goToPolicies,
  handleCloseZoneDrawer,
  selectedZoneRow,
  selectedZoneStatus,
  selectedZoneLoading,
  titleCase,
  handleValidateZone,
  openZonePolicies,
  handleOpenEdit,
  handleDuplicateZone,
  openPushForZone,
  beginDeleteZone,
  selectedZonePolicies,
  classError,
  zoneClassValue,
  setZoneClassValue,
  riskLevelValue,
  setRiskLevelValue,
  classSaving,
  saveClassification,
  cadenceError,
  cadenceValue,
  setCadenceValue,
  cadenceSaving,
  saveCadence,
  openApplyFeature,
  zoneFeatures,
  removeAssignment,
  applyFeatureOpen,
  setApplyFeatureOpen,
  applyTemplateId,
  setApplyTemplateId,
  featureTemplates,
  setApplyMinDwellMs,
  setApplyCooldownMs,
  setApplySeverity,
  applyMinDwellMs,
  applyCooldownMs,
  applySeverity,
  applyEnabled,
  setApplyEnabled,
  applyDeployNow,
  setApplyDeployNow,
  applyingFeature,
  applyFeature,
  snapshotEditorOpen,
  SITE_ID,
  initialDeviceId,
  initialCameraId,
  editorOpen,
  handleCloseEditor,
  editorMode,
  editingZone,
  yamlError,
  yamlContent,
  setYamlContent,
  handleSaveZone,
  pushDialogOpen,
  setPushDialogOpen,
  pushDeviceId,
  setPushDeviceId,
  pushing,
  handlePushToEdge,
  fetchData,
  showDisabledZones,
  setShowDisabledZones,
  pauseAutoRefresh,
  setPauseAutoRefresh,
  returnTo,
  handleBackFromZones,
  zoneMenuAnchor,
  zoneMenuZone,
  deleteDialogOpen,
  deleteSubmitting,
  setDeleteDialogOpen,
  zonePendingDelete,
  setZonePendingDelete,
  handleDeleteZone,
  snackbar,
  setSnackbar,
}) => (
  <Box sx={{ pb: 3 }}>
    <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 3, borderColor: '#e5e7eb' }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
            Configure
          </Typography>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Zone Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure where incidents are evaluated, then confirm geometry, linked policies, and edge readiness before you deploy changes.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Chip label={`${zoneSummary.total} zones`} color="primary" />
            <Chip label={`${zoneSummary.active} ${CONFIG_STATE_LABELS.live.toLowerCase()}`} variant="outlined" />
            {zoneSummary.missingPolicies !== null ? (
              <Chip
                label={`${zoneSummary.missingPolicies} need policy links`}
                color={zoneSummary.missingPolicies > 0 ? 'warning' : 'success'}
                variant={zoneSummary.missingPolicies > 0 ? 'filled' : 'outlined'}
              />
            ) : null}
            <Chip
              label={`${zoneSummary.missingSetup} need geometry`}
              color={zoneSummary.missingSetup > 0 ? 'warning' : 'success'}
              variant={zoneSummary.missingSetup > 0 ? 'filled' : 'outlined'}
            />
            {zoneSummary.errorCount > 0 ? <Chip label={`${zoneSummary.errorCount} ${CONFIG_STATE_LABELS.blocked.toLowerCase()}`} color="error" /> : null}
            <Chip label={`${zoneSummary.lineCount} ${CONFIG_SCOPE_LABELS.line.toLowerCase()}s ${CONFIG_STATE_LABELS.configured.toLowerCase()}`} variant="outlined" />
          </Box>
          {zoneSummary.lastLiveUpdateLabel !== '—' ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
              Live status refreshed {zoneSummary.lastLiveUpdateLabel}
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" onClick={() => setSnapshotEditorOpen(true)} disabled={!canWriteZones}>
            Snapshot Editor
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} disabled={!canWriteZones}>
            Add Zone
          </Button>
          <Button variant="contained" color="success" startIcon={<PushIcon />} onClick={handleOpenPush} disabled={!canWriteZones}>
            Push to Edge
          </Button>
          <IconButton onClick={(event) => setPageMenuAnchor(event.currentTarget)} aria-label="More zone actions">
            <MoreVertIcon />
          </IconButton>
        </Stack>
      </Box>
    </Paper>

    {!!siteComplexity?.pilot_mode && (siteComplexity.warnings || []).length > 0 && (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Complexity warning.</strong> Pilot-mode scoring flagged {siteComplexity.warnings.length}{' '}
        configuration signal{siteComplexity.warnings.length === 1 ? '' : 's'} for review before the next deploy.
      </Alert>
    )}

    {canReadZones && !canWriteZones && (
      <Alert severity="info" sx={{ mb: 2 }}>
        Read-only zone access. Viewing is available, but create, edit, delete, deploy, and push actions are disabled.
      </Alert>
    )}

    <Alert severity="info" sx={{ mb: 2 }}>
      Zones define where incidents are evaluated. Each {CONFIG_STATE_LABELS.live.toLowerCase()} zone should have geometry and at least one policy. Use
      Snapshot Editor for geometry and line configuration, then push to edge when changes are ready.
    </Alert>
    <Alert severity="info" sx={{ mb: 2 }}>
      Zone status shows configuration readiness, not incident severity. A zone only affects live incident review after geometry, linked
      policies, and push-to-edge are complete.
    </Alert>

    {(zoneSummary.missingSetup > 0 || (zoneSummary.missingPolicies || 0) > 0) && (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {[
          zoneSummary.missingPolicies && zoneSummary.missingPolicies > 0
            ? `${zoneSummary.missingPolicies} zone${zoneSummary.missingPolicies === 1 ? '' : 's'} need policy links`
            : null,
          zoneSummary.missingSetup > 0
            ? `${zoneSummary.missingSetup} zone${zoneSummary.missingSetup === 1 ? '' : 's'} still need geometry`
            : null,
        ]
          .filter(Boolean)
          .join('. ')}
        . These zones will not be fully operational until setup is completed.
      </Alert>
    )}

    {refreshing && !loading && <LinearProgress sx={{ mb: 2 }} />}

    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          label="Search zones"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
        />
        <WarehouseSelect
          label="Status"
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as typeof statusFilter)}
          minWidth={160}
          options={[
            { value: 'all', label: 'All' },
            { value: 'active', label: CONFIG_STATE_LABELS.live },
            { value: 'needs_policy', label: `${CONFIG_STATE_LABELS.needsAction}: link policies` },
            { value: 'needs_setup', label: `${CONFIG_STATE_LABELS.needsAction}: add geometry` },
            { value: 'error', label: CONFIG_STATE_LABELS.blocked },
            { value: 'draft', label: CONFIG_STATE_LABELS.draft },
          ]}
        />
        <WarehouseSelect
          label="Type"
          value={typeFilter}
          onChange={setTypeFilter}
          minWidth={160}
          options={[
            { value: 'all', label: 'All types' },
            ...zoneTypeOptions.map((option) => ({ value: option, label: option })),
          ]}
        />
        <WarehouseSelect
          label="Snapshot"
          value={snapshotFilter}
          onChange={(value) => setSnapshotFilter(value as typeof snapshotFilter)}
          minWidth={170}
          options={[
            { value: 'all', label: 'All' },
            { value: 'configured', label: CONFIG_STATE_LABELS.configured },
            { value: 'missing', label: CONFIG_STATE_LABELS.missing },
          ]}
        />
        <WarehouseSelect
          label="Policies"
          value={policyFilter}
          disabled={!policiesLoaded}
          onChange={(value) => setPolicyFilter(value as typeof policyFilter)}
          minWidth={170}
          options={[
            { value: 'all', label: 'All' },
            { value: 'has_policies', label: 'Has policies' },
            { value: 'no_policies', label: 'No policies' },
          ]}
        />
        <Button variant="text" onClick={clearFilters}>
          Clear filters
        </Button>
      </Box>
    </Paper>

    {zonesLoaded && zoneSummary.total === 0 ? (
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, textAlign: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          No zones configured
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mb: 2 }}>
          Zones are the bridge between camera setup and policies. Define areas and lines first, then return to Policies
          to stage behaviors and later validate them in Preflight.
        </Typography>
        <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} disabled={!canWriteZones}>
            Add Zone
          </Button>
          <Button variant="outlined" onClick={() => setSnapshotEditorOpen(true)} disabled={!canWriteZones}>
            Open Snapshot Editor
          </Button>
          <Button variant="text" component="a" href="/setup-rollout">
            View setup journey
          </Button>
        </Box>
      </Paper>
    ) : (
      <>
        <ZoneTable
          rows={filteredZoneRows}
          loading={loading && !zonesLoaded}
          selectedZoneId={selectedZone?.id || null}
          onSelectRow={(row: any) => handleZoneSelect(row.zone)}
          onOpenActions={(event: React.MouseEvent<HTMLElement>, row: any) => {
            setZoneMenuAnchor(event.currentTarget);
            setZoneMenuZone(row.zone);
          }}
        />

        {policiesLoaded && zoneSummary.total > 0 && zoneSummary.missingPolicies === zoneSummary.active ? (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Zones need action
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
              Zones define where safety logic applies. Attach policies to start detecting incidents in these areas.
            </Typography>
            <Button
              variant="contained"
              disabled={!canWriteZones}
              onClick={() => {
                const firstRow = zoneRows.find((row: any) => row.hasPolicies === false);
                if (firstRow) createPolicyForZone(firstRow.zone);
              }}
            >
              Create policy
            </Button>
          </Paper>
        ) : null}

        {filteredZoneRows.length === 0 && zoneSummary.total > 0 ? (
          <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              No zones match the current filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
              Clear one or more filters to return to the full zone list.
            </Typography>
            <Button variant="outlined" onClick={clearFilters}>
              Clear filters
            </Button>
          </Paper>
        ) : null}
      </>
    )}

    <Accordion
      expanded={linesExpanded}
      onChange={(_, expanded) => setLinesExpanded(expanded)}
      sx={{ mt: 2, borderRadius: '12px !important', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>Line Configuration</Typography>
          <Typography variant="body2" color="text.secondary">
            {lines.length > 0
              ? `${lines.length} ${CONFIG_SCOPE_LABELS.line.toLowerCase()}${lines.length === 1 ? '' : 's'} ${CONFIG_STATE_LABELS.configured.toLowerCase()} in Snapshot Editor`
              : `No ${CONFIG_SCOPE_LABELS.line.toLowerCase()}s ${CONFIG_STATE_LABELS.configured.toLowerCase()} yet`}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {lines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No lines yet. Open Snapshot Editor and use the Lines mode to draw them when line-crossing policies are needed.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Line</TableCell>
                  <TableCell>{CONFIG_SCOPE_LABELS.cameraStream}</TableCell>
                  <TableCell>Policies</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {line.name || line.line_id || 'Line'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {line.line_id || line.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{line.camera_id || line.device_id || '—'}</TableCell>
                    <TableCell>{policyUsage.lineCount[line.line_id] || 0} {CONFIG_STATE_LABELS.live.toLowerCase()}</TableCell>
                    <TableCell>{formatDateTime(line.updated_at)}</TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={0.5} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            goToPolicies({
                              type: 'line_crossing',
                              camera_id: String((line as any)?.camera_id || ''),
                              line_id: line.line_id,
                            })
                          }
                        >
                          View policies
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!canWriteZones}
                          onClick={() =>
                            goToPolicies({
                              create: '1',
                              type: 'line_crossing',
                              camera_id: String((line as any)?.camera_id || ''),
                              line_id: line.line_id,
                            })
                          }
                        >
                          Create policy
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>

    <Drawer
      anchor="right"
      open={!!selectedZone}
      onClose={handleCloseZoneDrawer}
      PaperProps={{ sx: { width: { xs: '100%', md: 560 }, p: 2.5 } }}
    >
      {selectedZone && (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap>
                {selectedZoneRow?.displayName || selectedZone.name || selectedZone.zone_id}
              </Typography>
              {!!selectedZoneRow?.secondaryId && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {selectedZoneRow.secondaryId}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleCloseZoneDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Review current state, policy coverage, and operational details without losing the main zone list.
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <ZoneStatusBadge label={selectedZoneRow?.statusLabel || CONFIG_STATE_LABELS.live} />
            <ZoneStatusBadge
              label={selectedZoneRow?.geometryConfigured ? `Snapshot ${CONFIG_STATE_LABELS.configured.toLowerCase()}` : CONFIG_STATE_LABELS.missing}
              kind="snapshot"
            />
            <ZoneStatusBadge
              label={
                selectedZoneRow?.hasPolicies === false
                  ? 'No policies'
                  : selectedZoneRow?.hasPolicies === true
                    ? 'Has policies'
                    : 'Policies loading'
              }
              kind="policy"
            />
          </Box>

          {selectedZoneLoading ? <LinearProgress /> : null}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Summary</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Typography variant="body2">{selectedZoneRow?.statusLabel || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body2">{titleCase(selectedZone.zone_type || selectedZone.type || 'unknown')}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Snapshot
                </Typography>
                <Typography variant="body2">
                  {selectedZoneRow?.geometryConfigured ? CONFIG_STATE_LABELS.configured : CONFIG_STATE_LABELS.missing}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Policy coverage
                </Typography>
                <Typography variant="body2">
                  {selectedZoneRow?.policyCount === null
                    ? 'Loading…'
                    : selectedZoneRow?.policyCount
                      ? `${selectedZoneRow.policyCount} ${CONFIG_STATE_LABELS.live.toLowerCase()}`
                      : 'No policies'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Occupancy
                </Typography>
                <Typography variant="body2">{selectedZoneRow?.occupancyLabel || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last updated
                </Typography>
                <Typography variant="body2">{selectedZoneRow?.lastUpdatedLabel || '—'}</Typography>
              </Box>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Actions</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" startIcon={<ValidateIcon />} onClick={() => handleValidateZone(selectedZone)}>
                Validate zone
              </Button>
              <Button size="small" variant="outlined" startIcon={<ViewIcon />} onClick={() => openZonePolicies(selectedZone)}>
                View policies
              </Button>
              <Button size="small" variant="contained" onClick={() => createPolicyForZone(selectedZone)} disabled={!canWriteZones}>
                Create policy
              </Button>
              <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleOpenEdit(selectedZone)} disabled={!canWriteZones}>
                Edit zone
              </Button>
              <Button size="small" variant="outlined" onClick={() => handleDuplicateZone(selectedZone)} disabled={!canWriteZones}>
                Duplicate zone
              </Button>
              <Button size="small" variant="outlined" startIcon={<PushIcon />} onClick={() => openPushForZone(selectedZone)} disabled={!canWriteZones}>
                Push to edge
              </Button>
              {!selectedZoneRow?.geometryConfigured ? (
                <Button size="small" variant="outlined" onClick={() => setSnapshotEditorOpen(true)} disabled={!canWriteZones}>
                  Open Snapshot Editor
                </Button>
              ) : null}
              <Button size="small" color="error" variant="contained" startIcon={<DeleteIcon />} onClick={() => beginDeleteZone(selectedZone)} disabled={!canWriteZones}>
                Delete zone
              </Button>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Configuration</Typography>
            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemText primary="Zone ID" secondary={selectedZone.zone_id || '—'} />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary={CONFIG_SCOPE_LABELS.cameraStream} secondary={selectedZone.camera_id || '—'} />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary={CONFIG_SCOPE_LABELS.edgeDevice} secondary={selectedZone.device_id || '—'} />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Geometry"
                  secondary={
                    selectedZoneRow?.geometryConfigured
                      ? selectedZone.bbox_norm
                        ? `[${selectedZone.bbox_norm.map((value: number) => value.toFixed(2)).join(', ')}]`
                        : `Polygon ${CONFIG_STATE_LABELS.configured.toLowerCase()}`
                      : `Missing ${CONFIG_SCOPE_LABELS.area.toLowerCase()} geometry`
                  }
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Line configuration"
                  secondary={
                    lines.length > 0
                      ? `${lines.length} global ${CONFIG_SCOPE_LABELS.line.toLowerCase()}${lines.length === 1 ? '' : 's'} ${CONFIG_STATE_LABELS.configured.toLowerCase()} in Snapshot Editor`
                      : `No ${CONFIG_SCOPE_LABELS.line.toLowerCase()}s ${CONFIG_STATE_LABELS.configured.toLowerCase()}`
                  }
                />
              </ListItem>
            </List>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Policies</Typography>
            {selectedZonePolicies.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                This zone does not have policy coverage yet.
              </Alert>
            ) : null}
            {policiesLoaded ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {selectedZonePolicies.length > 0 ? (
                  selectedZonePolicies.map((policy) => (
                    <Chip key={policy.id} label={policy.name || policy.policy_id} variant="outlined" size="small" />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No linked policies.
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Loading policy coverage…
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
              <Button size="small" variant="contained" onClick={() => createPolicyForZone(selectedZone)} disabled={!canWriteZones}>
                Create policy for this zone
              </Button>
              <Button size="small" variant="outlined" onClick={() => openZonePolicies(selectedZone)}>
                View all linked policies
              </Button>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Classification</Typography>
            {classError ? <Alert severity="error" sx={{ mb: 1 }}>{classError}</Alert> : null}
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <WarehouseSelect
                label="Zone class"
                value={zoneClassValue}
                onChange={(value) => setZoneClassValue(value as typeof zoneClassValue)}
                minWidth={180}
                options={[
                  { value: 'general', label: 'general' },
                  { value: 'high_risk', label: 'high_risk' },
                ]}
              />
              <WarehouseSelect
                label="Risk level"
                value={riskLevelValue}
                onChange={(value) => setRiskLevelValue(value)}
                minWidth={160}
                emptyOption={{ value: '', label: '(unset)' }}
                options={[
                  { value: 'low', label: 'low' },
                  { value: 'medium', label: 'medium' },
                  { value: 'high', label: 'high' },
                  { value: 'critical', label: 'critical' },
                ]}
              />
              <Button size="small" variant="outlined" disabled={!canWriteZones || classSaving || !selectedZone?.id} onClick={saveClassification}>
                Save
              </Button>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Notification cadence</Typography>
            {cadenceError ? <Alert severity="error" sx={{ mb: 1 }}>{cadenceError}</Alert> : null}
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <WarehouseSelect
                label="Cadence"
                value={cadenceValue}
                onChange={(value) => setCadenceValue((value || 'realtime') as typeof cadenceValue)}
                minWidth={220}
                options={[
                  { value: 'realtime', label: 'Realtime (ticket per incident)' },
                  { value: 'daily', label: 'Daily digest' },
                  { value: 'weekly', label: 'Weekly digest' },
                  { value: 'none', label: 'None (log only)' },
                ]}
              />
              <Button size="small" variant="outlined" disabled={!canWriteZones || cadenceSaving || !selectedZone?.id} onClick={saveCadence}>
                Save
              </Button>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
              <Typography sx={{ fontWeight: 700 }}>Applied features</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={openApplyFeature} disabled={!canWriteZones}>
                Apply feature
              </Button>
            </Box>
            {(selectedZoneStatus?.active_alerts || 0) > 0 ? (
              <Chip icon={<WarningIcon />} label={`${selectedZoneStatus?.active_alerts} active event signals`} color="error" sx={{ mt: 1.25 }} />
            ) : (
              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1.25 }}>
                <OkIcon color="success" />
                <Typography variant="body2" color="success.main">
                  No active event signals
                </Typography>
              </Box>
            )}
            {zoneFeatures.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
                No features applied. Use “Apply Feature” to deploy a capability to this zone.
              </Typography>
            ) : (
              <List dense sx={{ mt: 1 }}>
                {zoneFeatures.map((assignment) => (
                  <ListItem
                    key={assignment.id}
                    disableGutters
                    secondaryAction={
                      <Button size="small" color="error" onClick={() => removeAssignment(assignment.id)} disabled={!canWriteZones}>
                        Remove
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={`${assignment.template_name} (${assignment.template_version})`}
                      secondary={`enabled=${assignment.enabled ? 'true' : 'false'} · dwell=${Math.round(Number((assignment.effective_parameters as any)?.min_dwell_ms ?? 0) / 1000)}s · cooldown=${Math.round(Number((assignment.effective_parameters as any)?.cooldown_ms ?? 0) / 1000)}s · severity=${(assignment.effective_parameters as any)?.severity ?? '—'}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          <Accordion disableGutters sx={{ '&:before': { display: 'none' }, borderRadius: '12px !important' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Technical metadata</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Raw IDs, timestamps, and payloads for support and debugging.
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: 'grey.50',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  overflow: 'auto',
                }}
              >
                {JSON.stringify({ zone: selectedZone, status: selectedZoneStatus, features: zoneFeatures }, null, 2)}
              </Paper>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Drawer>

    <Dialog open={applyFeatureOpen} onClose={() => setApplyFeatureOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Apply Feature to Zone</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This will compile into runtime policies and deploy via the normal Deployments pipeline.
        </Alert>

        <Box sx={{ mb: 2 }}>
          <WarehouseSelect
            id="feature-template-picker"
            label="Feature"
            value={applyTemplateId}
            onChange={(nextId) => {
              setApplyTemplateId(nextId);
              const tpl = featureTemplates.find((t) => t.id === nextId);
              const defaults = (tpl?.default_parameters || {}) as any;
              setApplyMinDwellMs(Number(defaults?.min_dwell_ms ?? 2000));
              setApplyCooldownMs(Number(defaults?.cooldown_ms ?? 60000));
              setApplySeverity(String(defaults?.severity ?? 'high'));
            }}
            minWidth="100%"
            formControlSx={{ width: '100%' }}
            options={featureTemplates.map((t) => ({
              value: t.id,
              label: `${t.name} (${t.version})`,
            }))}
          />
        </Box>

        <TextField
          fullWidth
          label="Min dwell (ms)"
          type="number"
          value={applyMinDwellMs}
          onChange={(e) => setApplyMinDwellMs(Number(e.target.value))}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Cooldown (ms)"
          type="number"
          value={applyCooldownMs}
          onChange={(e) => setApplyCooldownMs(Number(e.target.value))}
          sx={{ mb: 2 }}
        />
        <Box sx={{ mb: 2 }}>
          <WarehouseSelect
            id="feature-severity-picker"
            label="Severity"
            value={applySeverity}
            onChange={setApplySeverity}
            minWidth="100%"
            formControlSx={{ width: '100%' }}
            options={['low', 'medium', 'high', 'critical'].map((s) => ({ value: s, label: s }))}
          />
        </Box>

        <FormControlLabel control={<Checkbox checked={applyEnabled} onChange={(e) => setApplyEnabled(e.target.checked)} />} label="Enabled" />
        <FormControlLabel control={<Checkbox checked={applyDeployNow} onChange={(e) => setApplyDeployNow(e.target.checked)} />} label="Deploy now" />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setApplyFeatureOpen(false)} disabled={applyingFeature}>
          Cancel
        </Button>
        <Button variant="contained" onClick={applyFeature} disabled={!canWriteZones || applyingFeature}>
          {applyingFeature ? 'Applying…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>

    <ZoneSnapshotEditorDialog
      open={snapshotEditorOpen}
      onClose={() => setSnapshotEditorOpen(false)}
      siteId={SITE_ID}
      initialEdgeDeviceId={initialDeviceId}
      initialCameraId={initialCameraId}
    />

    <Dialog open={editorOpen} onClose={handleCloseEditor} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CodeIcon />
          {editorMode === 'create' ? 'Create New Zone' : `Edit Zone: ${editingZone?.name}`}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Edit the zone configuration below. Use YAML-like syntax. After saving, use "Push to Edge" to deploy changes
          to devices.
        </Alert>

        {yamlError && <Alert severity="error" sx={{ mb: 2 }}>{yamlError}</Alert>}

        <TextField
          fullWidth
          multiline
          rows={20}
          value={yamlContent}
          onChange={(e) => setYamlContent(e.target.value)}
          sx={{
            fontFamily: 'monospace',
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '14px',
              lineHeight: 1.5,
            },
          }}
          placeholder="# Zone configuration..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseEditor}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveZone} disabled={!canWriteZones}>
          {editorMode === 'create' ? 'Create Zone' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={pushDialogOpen} onClose={() => setPushDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PushIcon color="success" />
          Push Zones to Edge Device
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This will push all zone configurations to the edge device. The device will update its camera config and apply
          the new zones.
        </Alert>

        <TextField
          fullWidth
          label="Device ID"
          value={pushDeviceId}
          onChange={(e) => setPushDeviceId(e.target.value)}
          sx={{ mt: 2 }}
          helperText="The device ID to push zones to (e.g., rtsp_cam)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPushDialogOpen(false)}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handlePushToEdge}
          disabled={!canWriteZones || pushing || !pushDeviceId}
          startIcon={pushing ? <CircularProgress size={20} /> : <PushIcon />}
        >
          {pushing ? 'Pushing...' : 'Push to Edge'}
        </Button>
      </DialogActions>
    </Dialog>

    <Menu
      anchorEl={pageMenuAnchor}
      open={Boolean(pageMenuAnchor)}
      onClose={() => setPageMenuAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <MenuItem
        onClick={() => {
          setPageMenuAnchor(null);
          void fetchData(true);
        }}
      >
        <RefreshIcon fontSize="small" sx={{ mr: 1 }} />
        Refresh now
      </MenuItem>
      <MenuItem onClick={() => setShowDisabledZones((prev) => !prev)}>
        <Checkbox size="small" checked={showDisabledZones} sx={{ mr: 1 }} />
        Show disabled zones
      </MenuItem>
      <MenuItem onClick={() => setPauseAutoRefresh((prev) => !prev)}>
        <Checkbox size="small" checked={pauseAutoRefresh} sx={{ mr: 1 }} />
        Pause auto-refresh
      </MenuItem>
      {(returnTo === 'setup' || window.history.length > 1) && (
        <MenuItem
          onClick={() => {
            setPageMenuAnchor(null);
            handleBackFromZones();
          }}
        >
          Back
        </MenuItem>
      )}
      <MenuItem
        component="a"
        href="/docs/user-guide/configuration/zones/"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => setPageMenuAnchor(null)}
      >
        <DocsIcon fontSize="small" sx={{ mr: 1 }} />
        Open documentation
      </MenuItem>
    </Menu>

    <Menu
      anchorEl={zoneMenuAnchor}
      open={Boolean(zoneMenuAnchor)}
      onClose={() => {
        setZoneMenuAnchor(null);
        setZoneMenuZone(null);
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClick={() => {
        setZoneMenuAnchor(null);
        setZoneMenuZone(null);
      }}
    >
      <MenuItem
        onClick={() => {
          const z = zoneMenuZone;
          if (z) handleZoneSelect(z);
        }}
        disabled={!zoneMenuZone}
      >
        <ViewIcon fontSize="small" sx={{ mr: 1 }} />
        View
      </MenuItem>
      <MenuItem
        onClick={() => {
          const z = zoneMenuZone;
          if (z) void handleValidateZone(z);
        }}
        disabled={!zoneMenuZone}
      >
        <ValidateIcon fontSize="small" sx={{ mr: 1 }} />
        Validate
      </MenuItem>
      <MenuItem
        onClick={() => {
          const z = zoneMenuZone;
          if (z) openZonePolicies(z);
        }}
        disabled={!zoneMenuZone}
      >
        <ViewIcon fontSize="small" sx={{ mr: 1 }} />
        View policies
      </MenuItem>
      <MenuItem
        onClick={() => {
          const z = zoneMenuZone;
          if (z) createPolicyForZone(z);
        }}
        disabled={!canWriteZones || !zoneMenuZone}
      >
        <AddIcon fontSize="small" sx={{ mr: 1 }} />
        Create policy
      </MenuItem>
      <MenuItem
        onClick={() => {
          const z = zoneMenuZone;
          if (z) handleOpenEdit(z);
        }}
        disabled={!canWriteZones || !zoneMenuZone}
      >
        <EditIcon fontSize="small" sx={{ mr: 1 }} />
        Edit zone
      </MenuItem>
      <MenuItem
        onClick={() => {
          const z = zoneMenuZone;
          if (z) handleDuplicateZone(z);
        }}
        disabled={!canWriteZones || !zoneMenuZone}
      >
        <CodeIcon fontSize="small" sx={{ mr: 1 }} />
        Duplicate zone
      </MenuItem>
      <MenuItem
        onClick={() => {
          const z = zoneMenuZone;
          if (z) beginDeleteZone(z);
        }}
        disabled={!canWriteZones || !zoneMenuZone}
        sx={{ color: 'error.main' }}
      >
        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
        Delete
      </MenuItem>
    </Menu>

    <Dialog
      open={deleteDialogOpen}
      onClose={() => {
        if (deleteSubmitting) return;
        setDeleteDialogOpen(false);
        setZonePendingDelete(null);
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Disable zone in cloud?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          This does not hard-delete geometry: the API marks the zone <strong>disabled</strong> in cloud (soft delete).
          Edge overlays update after you push/deploy.
        </Typography>
        <Typography variant="body2">
          Zone: <strong>{zonePendingDelete?.name || zonePendingDelete?.zone_id || '—'}</strong>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            if (deleteSubmitting) return;
            setDeleteDialogOpen(false);
            setZonePendingDelete(null);
          }}
        >
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={!canWriteZones || deleteSubmitting || !zonePendingDelete}
          onClick={() => {
            if (zonePendingDelete) void handleDeleteZone(zonePendingDelete);
          }}
        >
          {deleteSubmitting ? 'Working…' : 'Disable / remove from list'}
        </Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={snackbar.open}
      autoHideDuration={snackbar.autoHideDuration ?? 4000}
      onClose={() => setSnackbar((s: any) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity={snackbar.severity} onClose={() => setSnackbar((s: any) => ({ ...s, open: false }))}>
        {snackbar.message}
      </Alert>
    </Snackbar>
  </Box>
);

export default ZonesPageContent;
